import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { 
  Calendar, MapPin, Users, Edit, Save, Loader2, RefreshCw, IndianRupee, ExternalLink
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminEvents = () => {
  const { getAuthAxios } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState([]);
  const [editingEvent, setEditingEvent] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [initializing, setInitializing] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    try {
      const authAxios = getAuthAxios();
      const response = await authAxios.get(`${API}/admin/events`);
      setEvents(response.data.events || []);
    } catch (error) {
      console.error("Error fetching events:", error);
      // If no events, show empty state with initialize option
    } finally {
      setLoading(false);
    }
  };

  const initializeEvents = async () => {
    setInitializing(true);
    try {
      const authAxios = getAuthAxios();
      await authAxios.post(`${API}/admin/events/initialize`);
      toast.success("Events initialized! You can now edit them.");
      fetchEvents();
    } catch (error) {
      toast.error("Failed to initialize events");
    } finally {
      setInitializing(false);
    }
  };

  const handleEdit = (event) => {
    setEditingEvent({ ...event });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    if (!editingEvent) return;
    
    setSaving(true);
    try {
      const authAxios = getAuthAxios();
      await authAxios.put(`${API}/admin/events/${editingEvent.id}`, editingEvent);
      toast.success("Event updated successfully!");
      setEditDialogOpen(false);
      fetchEvents();
    } catch (error) {
      toast.error("Failed to update event");
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setEditingEvent(prev => ({ ...prev, [field]: value }));
  };

  const updatePricing = (category, value) => {
    setEditingEvent(prev => ({
      ...prev,
      pricing: {
        ...prev.pricing,
        [category]: parseInt(value) || 0
      }
    }));
  };

  const upcomingEvents = events.filter(e => e.status === "upcoming");
  const pastEvents = events.filter(e => e.status === "completed");

  const EventCard = ({ event }) => (
    <Card className="hover:shadow-md transition-shadow">
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between">
          <div>
            <span className={`inline-block px-2 py-1 rounded-full text-xs font-bold mb-2 ${
              event.status === "upcoming" 
                ? "bg-green-100 text-green-700" 
                : "bg-gray-100 text-gray-700"
            }`}>
              {event.status === "upcoming" ? "UPCOMING" : "COMPLETED"}
            </span>
            <CardTitle className="text-lg">{event.name}</CardTitle>
            <CardDescription>{event.edition}</CardDescription>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => handleEdit(event)}
            data-testid={`edit-event-${event.id}`}
          >
            <Edit className="w-4 h-4 mr-1" />
            Edit
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2 text-[#4B5563]">
            <Calendar className="w-4 h-4 text-[#FF3B30]" />
            <span>{event.date}</span>
          </div>
          <div className="flex items-center gap-2 text-[#4B5563]">
            <MapPin className="w-4 h-4 text-[#007AFF]" />
            <span>{event.location}</span>
          </div>
          {event.participants && (
            <div className="flex items-center gap-2 text-[#4B5563]">
              <Users className="w-4 h-4 text-[#FFD700]" />
              <span>{event.participants} participants</span>
            </div>
          )}
          {event.pricing && (
            <div className="flex items-center gap-2 text-[#4B5563]">
              <IndianRupee className="w-4 h-4 text-green-600" />
              <span>3K: ₹{event.pricing["3k"]} | 5K: ₹{event.pricing["5k"]} | 10K: ₹{event.pricing["10k"]}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );

  return (
    <div data-testid="admin-events">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-3xl font-bold text-[#0A0A0A] font-['Outfit']">Event Details</h1>
          <p className="text-[#4B5563]">Manage event information, dates, locations, and pricing</p>
        </div>
        
        {events.length === 0 && (
          <Button 
            onClick={initializeEvents} 
            className="btn-primary"
            disabled={initializing}
          >
            {initializing ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Initializing...
              </>
            ) : (
              <>
                <RefreshCw className="w-4 h-4 mr-2" />
                Initialize Events
              </>
            )}
          </Button>
        )}
      </div>

      {loading ? (
        <div className="text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-[#FF3B30]" />
          <p className="mt-2 text-[#4B5563]">Loading events...</p>
        </div>
      ) : events.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-semibold text-[#0A0A0A] mb-2">No Events Found</h3>
            <p className="text-[#4B5563] mb-4">
              Click "Initialize Events" to load default events into the database for editing.
            </p>
            <Button onClick={initializeEvents} className="btn-primary" disabled={initializing}>
              {initializing ? "Initializing..." : "Initialize Events"}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-8">
          {/* Upcoming Events */}
          {upcomingEvents.length > 0 && (
            <div>
              <h2 className="text-xl font-bold text-[#0A0A0A] mb-4 flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-green-500" />
                Upcoming Events
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {upcomingEvents.map(event => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            </div>
          )}

          {/* Past Events */}
          {pastEvents.length > 0 && (
            <div>
              <h2 className="text-xl font-bold text-[#0A0A0A] mb-4 flex items-center gap-2">
                <span className="w-3 h-3 rounded-full bg-gray-400" />
                Past Events
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                {pastEvents.map(event => (
                  <EventCard key={event.id} event={event} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Event: {editingEvent?.name}</DialogTitle>
          </DialogHeader>
          
          {editingEvent && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Event Name</Label>
                  <Input 
                    value={editingEvent.name || ""}
                    onChange={(e) => updateField("name", e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Edition</Label>
                  <Input 
                    value={editingEvent.edition || ""}
                    onChange={(e) => updateField("edition", e.target.value)}
                    placeholder="e.g., 3rd Edition"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date</Label>
                  <Input 
                    value={editingEvent.date || ""}
                    onChange={(e) => updateField("date", e.target.value)}
                    placeholder="e.g., August 23, 2026"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input 
                    value={editingEvent.location || ""}
                    onChange={(e) => updateField("location", e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description</Label>
                <Textarea 
                  value={editingEvent.description || ""}
                  onChange={(e) => updateField("description", e.target.value)}
                  rows={3}
                />
              </div>

              {editingEvent.status === "completed" && (
                <>
                  <div className="space-y-2">
                    <Label>Participants Count</Label>
                    <Input 
                      value={editingEvent.participants || ""}
                      onChange={(e) => updateField("participants", e.target.value)}
                      placeholder="e.g., 5,000+"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label>External Gallery URL</Label>
                    <Input 
                      value={editingEvent.external_gallery_url || ""}
                      onChange={(e) => updateField("external_gallery_url", e.target.value)}
                      placeholder="Google Drive or external gallery link"
                    />
                  </div>
                </>
              )}

              {editingEvent.status === "upcoming" && editingEvent.pricing && (
                <div className="space-y-2">
                  <Label>Pricing (₹)</Label>
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">3K Fun Run</Label>
                      <Input 
                        type="number"
                        value={editingEvent.pricing["3k"] || 0}
                        onChange={(e) => updatePricing("3k", e.target.value)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">5K Timed</Label>
                      <Input 
                        type="number"
                        value={editingEvent.pricing["5k"] || 0}
                        onChange={(e) => updatePricing("5k", e.target.value)}
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">10K Timed</Label>
                      <Input 
                        type="number"
                        value={editingEvent.pricing["10k"] || 0}
                        onChange={(e) => updatePricing("10k", e.target.value)}
                      />
                    </div>
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-4">
                <Button 
                  onClick={handleSave} 
                  className="flex-1 btn-primary"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setEditDialogOpen(false)}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Help Card */}
      <Card className="mt-8">
        <CardHeader>
          <CardTitle>How It Works</CardTitle>
        </CardHeader>
        <CardContent className="text-[#4B5563]">
          <ul className="space-y-2">
            <li>• <strong>First time?</strong> Click "Initialize Events" to load events into the database</li>
            <li>• <strong>Edit</strong> any event's name, date, location, description, and pricing</li>
            <li>• <strong>Pricing</strong> changes will reflect on the registration page</li>
            <li>• <strong>Past events</strong> can have their gallery links and participant counts updated</li>
          </ul>
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminEvents;
